// Full-page marketplace keyword detail (issue #217): headline stats, one
// selected own-product rank history, channel rankings, and first-page evidence.

import { useEffect, useId, useRef, useState, type ReactNode, type Ref } from "react"
import { ChevronDown, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react"
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import {
  DASHBOARD_DATE_SELECTION_KIND,
  type DashboardDateSelection,
  type SearchRankingCapturePoint,
  type SearchRankingKeywordDetail,
  type SearchRankingTitleOpportunity,
} from "@nova-ai/shared"

import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion"
import { chartSeriesColorForKey } from "../../lib/marketplace-series"
import { cn } from "../../lib/utils"
import { useSearchRankingOpportunities } from "../../services/use-search-rankings"
import { DashboardDatePicker } from "../dashboard/period-picker"
import { CHART_TOOLTIP_CONTENT_STYLE } from "../ui/chart-style"
import { GymBeamMark } from "../ui/gymbeam-mark"
import { SegmentedControl } from "../ui/segmented-control"
import {
  RANK_CHANNELS,
  SEARCH_RANKING_DATE_PERIODS,
  buildProductChartSeries,
  dateRangeForSelection,
  dateSelectionIncludingDay,
  rankChartGranularity,
  filterRankHistory,
  latestDailyRankChange,
  measuredDays,
  selectableProductSeries,
  type ProductPeriodMovement,
  type RankChannel,
  type SelectableProductSeries,
  type SearchRankingDateRange,
} from "./search-ranking-keyword-detail.derive"

type DetailCard = SearchRankingKeywordDetail["cards"][number]

/** The keyword's best own-brand position, day over day. Spelled out because the
 *  card rows carry an identical-looking arrow that means something else. */
const KEYWORD_MOVE_HINT =
  "Our best organic position for this keyword, versus the previous measured day"

/** One product inside its own channel ranking. The keyword can improve while this
 *  card drops — a different product taking the better slot moves one and not the
 *  other. */
const CARD_MOVE_HINT =
  "This product's position within its own ranking, versus the previous measured day"

// Recharts measures the real width through ResizeObserver. A valid first-frame
// dimension only avoids its -1/-1 diagnostic before that observer fires; it is
// not the chart's final size.
const RANK_CHART_INITIAL_DIMENSION = { width: 1, height: 256 }
const TITLE_OPPORTUNITIES_SECTION_ID = "competitor-title-opportunities"
const TITLE_OPPORTUNITIES_CONTENT_ID = "competitor-title-opportunities-content"
export function SearchRankingKeywordDetailView({
  detail,
  onSelectRun,
  isSwitchingCapture = false,
}: {
  detail: SearchRankingKeywordDetail
  /** Absent when the caller renders a fixed capture — the snapshot stepper is
   *  then hidden rather than shown inert. */
  onSelectRun?: (runId: string) => void
  isSwitchingCapture?: boolean
}) {
  const organic = detail.cards
    .filter((card) => !card.sponsored)
    .sort((a, b) => (a.organicRank ?? 0) - (b.organicRank ?? 0))
  const paid = detail.cards
    .filter((card) => card.sponsored)
    .sort((a, b) => (a.sponsoredRank ?? 0) - (b.sponsoredRank ?? 0))
  const ownBrandCount = detail.cards.filter((card) => card.isOwnBrand).length
  const bestOrganic = organic.find((card) => card.isOwnBrand)?.organicRank ?? null
  const bestAd = paid.find((card) => card.isOwnBrand)?.sponsoredRank ?? null
  const [dateSelection, setDateSelection] = useState<DashboardDateSelection>({
    kind: DASHBOARD_DATE_SELECTION_KIND.rolling,
    period: "30d",
  })
  const [channel, setChannel] = useState<RankChannel>("organic")
  const [selectedProductKey, setSelectedProductKey] = useState<string>()
  const [chartFocusRequest, setChartFocusRequest] = useState(0)
  const trendPanelRef = useRef<HTMLElement>(null)
  const activeRange = dateRangeForSelection(detail.history, dateSelection, detail.availableRange)
  const opportunities = useSearchRankingOpportunities(
    detail.marketplace.id,
    detail.phrase,
    activeRange
  )
  const prefersReducedMotion = usePrefersReducedMotion()
  const [areOpportunitiesExpanded, setAreOpportunitiesExpanded] = useState(false)
  const openTitleOpportunities = () => {
    setAreOpportunitiesExpanded(true)
    document.getElementById(TITLE_OPPORTUNITIES_SECTION_ID)?.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    })
  }

  useEffect(() => {
    setChannel("organic")
    setSelectedProductKey(undefined)
    setChartFocusRequest(0)
  }, [detail.marketplace.id, detail.phrase])

  useEffect(() => {
    if (chartFocusRequest === 0) {
      return
    }
    trendPanelRef.current?.focus({ preventScroll: true })
    trendPanelRef.current?.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    })
  }, [chartFocusRequest, prefersReducedMotion])

  const selectSnapshotProduct = (card: DetailCard) => {
    if (card.rankProductKey === null) {
      return
    }
    const captureDay = detail.history.find((point) => point.runId === detail.runId)?.day
    if (captureDay !== undefined) {
      setDateSelection(
        dateSelectionIncludingDay(detail.history, dateSelection, detail.availableRange, captureDay)
      )
    }
    setChannel(card.sponsored ? "sponsored" : "organic")
    setSelectedProductKey(card.rankProductKey)
    setChartFocusRequest((request) => request + 1)
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile
          label="Our best organic"
          value={bestOrganic === null ? "—" : `#${bestOrganic}`}
          sub={bestOrganic === null ? "not on page 1" : `of ${organic.length} organic results`}
          change={organicChange(detail.history)}
          changeHint={KEYWORD_MOVE_HINT}
        />

        <StatTile
          label="Our best ad"
          value={bestAd === null ? "—" : `#${bestAd}`}
          sub={
            bestAd === null
              ? `no ${detail.marketplace.sponsoredLabel.toLowerCase()} card today`
              : `of ${paid.length} paid slots`
          }
        />

        <StatTile
          label="GymBeam cards"
          value={String(ownBrandCount)}
          sub={`of ${detail.cards.length} cards on the first page`}
        />
      </div>

      <TrendPanel
        history={detail.history}
        timeZone={detail.marketplace.reportingTimeZone}
        sponsoredLabel={detail.marketplace.sponsoredLabel}
        productHistory={detail.productHistory}
        availableRange={detail.availableRange}
        dateSelection={dateSelection}
        onDateSelectionChange={setDateSelection}
        opportunities={opportunities.data?.opportunities}
        isOpportunitiesLoading={opportunities.isLoading}
        isOpportunitiesError={opportunities.isError}
        onOpenOpportunities={openTitleOpportunities}
        channel={channel}
        onChannelChange={setChannel}
        selectedProductKey={selectedProductKey}
        onSelectedProductKeyChange={setSelectedProductKey}
        sectionRef={trendPanelRef}
      />

      <TitleOpportunitiesPanel
        opportunities={opportunities.data?.opportunities}
        isLoading={opportunities.isLoading}
        isError={opportunities.isError}
        range={activeRange}
        expanded={areOpportunitiesExpanded}
        onExpandedChange={setAreOpportunitiesExpanded}
      />

      <FirstPagePanel
        detail={detail}
        organicCount={organic.length}
        paidCount={paid.length}
        {...(onSelectRun ? { onSelectRun } : {})}
        onSelectProduct={selectSnapshotProduct}
        isSwitching={isSwitchingCapture}
      />

      <p className="text-xs text-muted-foreground">
        {`Captured ${detail.capturedAt.slice(0, 16).replace("T", " ")} · run ${detail.runId}`}
      </p>
    </div>
  )
}

function StatTile({
  label,
  value,
  sub,
  change,
  changeHint,
}: {
  label: string
  value: string
  sub: string
  change?: number | null
  changeHint?: string
}) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3 shadow-card">
      <div className="text-xs text-muted-foreground">{label}</div>

      <div className="flex items-baseline gap-2">
        <span className="text-xl font-semibold tabular-nums">{value}</span>

        {change !== undefined && change !== null && change !== 0 ? (
          <span
            className={cn(
              "text-xs font-semibold tabular-nums",
              change > 0 ? "text-success" : "text-destructive"
            )}
            {...(changeHint ? { title: changeHint } : {})}
          >
            {change > 0 ? `▲ ${change}` : `▼ ${-change}`}
          </span>
        ) : null}
      </div>

      <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>
    </div>
  )
}

/** Keyword-level, day over day — the same number the overview's Change column
 *  renders. This page's series is per capture, so it has to be collapsed first or
 *  the tile would show an intraday move while the overview shows a daily one. */
function organicChange(history: SearchRankingCapturePoint[]): number | null {
  return latestDailyRankChange(history)
}

export function TrendPanel({
  history,
  timeZone,
  sponsoredLabel,
  productHistory,
  availableRange,
  dateSelection,
  onDateSelectionChange,
  opportunities,
  isOpportunitiesLoading = false,
  isOpportunitiesError = false,
  onOpenOpportunities,
  channel,
  onChannelChange,
  selectedProductKey,
  onSelectedProductKeyChange,
  sectionRef,
}: {
  history: SearchRankingCapturePoint[]
  timeZone: string
  sponsoredLabel: string
  productHistory: SearchRankingKeywordDetail["productHistory"]
  availableRange: SearchRankingDateRange
  dateSelection: DashboardDateSelection
  onDateSelectionChange: (selection: DashboardDateSelection) => void
  opportunities?: SearchRankingTitleOpportunity[] | undefined
  isOpportunitiesLoading?: boolean
  isOpportunitiesError?: boolean
  onOpenOpportunities?: () => void
  channel: RankChannel
  onChannelChange: (channel: RankChannel) => void
  selectedProductKey: string | undefined
  onSelectedProductKeyChange: (rankProductKey: string | undefined) => void
  sectionRef?: Ref<HTMLElement>
}) {
  const descriptionId = useId()
  const activeRange = dateRangeForSelection(history, dateSelection, availableRange)
  const visibleHistory = activeRange ? filterRankHistory(history, activeRange) : history
  const granularity = rankChartGranularity(activeRange)
  const paid = channel === "sponsored"
  const eligibleProducts = selectableProductSeries(
    visibleHistory,
    productHistory,
    channel,
    granularity
  )
  const selectedProduct = selectedProductOf(eligibleProducts, selectedProductKey)
  const visibleProducts = selectedProduct
    ? productHistory.filter((product) => product.rankProductKey === selectedProduct.rankProductKey)
    : []
  // A range or channel switch can make the prior product ineligible. Render the
  // deterministic fallback immediately, then commit it so returning to the old
  // range does not unexpectedly restore a stale selection.
  useEffect(() => {
    const nextKey = selectedProduct?.rankProductKey
    if (selectedProductKey !== nextKey) {
      onSelectedProductKeyChange(nextKey)
    }
  }, [onSelectedProductKeyChange, selectedProduct?.rankProductKey, selectedProductKey])

  return (
    <section
      ref={sectionRef}
      tabIndex={-1}
      aria-labelledby="product-ranking-trend-title"
      aria-describedby={descriptionId}
      className="scroll-mt-6 rounded-xl border border-border bg-card p-5 shadow-card outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
    >
      <header className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h3 id="product-ranking-trend-title" className="text-[15px] font-semibold tracking-tight">
            {paid ? `${sponsoredLabel} position trend` : "Organic position trend"}
          </h3>

          <p id={descriptionId} className="mt-1 text-xs text-muted-foreground">
            {selectedProduct
              ? `Ranking history for ${selectedProduct.name} · #1 is the first ${paid ? "paid slot" : "unpaid result"}`
              : `No GymBeam product has a ${paid ? "paid" : "organic"} rank in this range`}
          </p>
        </div>

        <div className="flex max-w-full flex-wrap items-center gap-2">
          <SegmentedControl
            options={RANK_CHANNELS.map(({ value, label }) => ({
              value,
              label: value === "sponsored" ? sponsoredLabel : label,
            }))}
            value={channel}
            onChange={onChannelChange}
            label="Ranking"
            className="shrink-0"
          />

          {availableRange ? (
            <DashboardDatePicker
              value={dateSelection}
              onChange={onDateSelectionChange}
              availableRange={availableRange}
              periods={SEARCH_RANKING_DATE_PERIODS}
              label="Ranking date range"
            />
          ) : null}
        </div>
      </header>

      <div className="mt-4 grid grid-cols-[minmax(0,1fr)] gap-5 border-t border-border/70 pt-4 xl:grid-cols-[minmax(0,1fr)_17rem]">
        <div className="min-w-0">
          <SelectedProductSummary product={selectedProduct} channel={channel} range={activeRange} />

          <RankHistoryChart
            history={visibleHistory}
            products={visibleProducts}
            granularity={granularity}
            timeZone={timeZone}
            channel={channel}
          />
        </div>

        <RankingInsightsRail
          opportunities={opportunities}
          isOpportunitiesLoading={isOpportunitiesLoading}
          isOpportunitiesError={isOpportunitiesError}
          onOpenOpportunities={onOpenOpportunities}
        />
      </div>
    </section>
  )
}

function SelectedProductSummary({
  product,
  channel,
  range,
}: {
  product: SelectableProductSeries | undefined
  channel: RankChannel
  range: SearchRankingDateRange | null
}) {
  if (!product) {
    return (
      <div className="text-xs text-muted-foreground">
        {`No ${channel === "sponsored" ? "paid" : "organic"} product rank in this range`}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2 text-xs sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <a
          href={product.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex max-w-full items-center gap-1 font-medium text-foreground hover:underline"
        >
          <span className="truncate">{product.name}</span>

          <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
        </a>

        <div className="mt-1">
          {product.matchedSku ? (
            <a
              href={`/products/${encodeURIComponent(product.matchedSku)}`}
              className="font-medium text-brand hover:underline"
            >
              {`SKU ${product.matchedSku}`}
            </a>
          ) : (
            <span className="text-muted-foreground">SKU not matched</span>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground sm:justify-end">
        <span>{`Current ${productRankLabel(product.currentRank)} · Best #${product.bestRank}`}</span>

        <ProductPeriodMovementLabel movement={product.periodMovement} />

        <span>{range ? `${formatDate(range.from)}–${formatDate(range.to)}` : ""}</span>
      </div>
    </div>
  )
}

function ProductPeriodMovementLabel({ movement }: { movement: ProductPeriodMovement }) {
  if (movement.kind === "entered") {
    return <span className="font-semibold text-success">Entered page 1</span>
  }
  if (movement.kind === "left") {
    return <span className="font-semibold text-destructive">Left page 1</span>
  }
  if (movement.kind === "off-page") {
    return <span>Off page at period start and end</span>
  }
  if (movement.kind === "insufficient") {
    return <span>No prior point in range</span>
  }
  if (movement.delta === 0) {
    return <span>No change over period</span>
  }
  return (
    <span
      className={cn(
        "font-semibold tabular-nums",
        movement.delta > 0 ? "text-success" : "text-destructive"
      )}
    >
      {movement.delta > 0 ? `▲ ${movement.delta} over period` : `▼ ${-movement.delta} over period`}
    </span>
  )
}

function selectedProductOf(
  products: SelectableProductSeries[],
  selectedProductKey: string | undefined
): SelectableProductSeries | undefined {
  return products.find((product) => product.rankProductKey === selectedProductKey) ?? products[0]
}

function productRankLabel(rank: number | null): string {
  return rank === null ? "Off page" : `#${rank}`
}

// Inverted rank chart (#1 on top). A single day plots every capture; wider
// ranges keep each day's last usable capture. Failed captures stay in the data
// as null values, so they break every line instead of becoming false movement.
function RankHistoryChart({
  history,
  products,
  granularity,
  timeZone,
  channel,
}: {
  history: SearchRankingCapturePoint[]
  products: SearchRankingKeywordDetail["productHistory"]
  granularity: "capture" | "day"
  timeZone: string
  channel: RankChannel
}) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const perCapture = granularity === "capture"
  const series = buildProductChartSeries(history, products, channel, granularity)
  const anchors = series[0]?.values ?? []
  if (series.length === 0 || anchors.every((point) => !point.measured)) {
    let emptyMessage = "No measured days in this range."
    if (series.length === 0) {
      emptyMessage = `No ${channel === "sponsored" ? "paid" : "organic"} product rank in this range.`
    } else if (perCapture) {
      emptyMessage = "No measured captures in this range."
    }
    return (
      <p className="flex h-64 items-center justify-center text-sm text-muted-foreground">
        {emptyMessage}
      </p>
    )
  }
  const chartRows = anchors.map((anchor, index) => {
    const row: Record<string, string | number | boolean | null> = {
      capturedAt: anchor.capturedAt,
      day: anchor.day,
      measured: anchor.measured,
    }
    for (const product of series) {
      row[product.rankProductKey] = product.values[index]?.rank ?? null
    }
    return row
  })
  const knownRanks = series
    .flatMap((product) => product.values.map((point) => point.rank))
    .filter((rank): rank is number => rank !== null)
  const yMax = Math.max(10, ...(knownRanks.length > 0 ? knownRanks : [1]))
  const ticks = [...new Set([1, Math.round((yMax + 1) / 2), yMax])]
  const nameByKey = new Map(series.map((product) => [product.rankProductKey, product.name]))

  return (
    <div
      className="mt-3 h-64 w-full"
      role="img"
      aria-label={channel === "sponsored" ? "Paid slot per capture" : "Organic rank per capture"}
    >
      <ResponsiveContainer
        width="100%"
        height="100%"
        initialDimension={RANK_CHART_INITIAL_DIMENSION}
      >
        <LineChart data={chartRows} margin={{ top: 12, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke="var(--color-border)" strokeDasharray="3 4" />

          <XAxis
            dataKey="capturedAt"
            axisLine={false}
            tickLine={false}
            minTickGap={28}
            tick={{ fill: "var(--color-muted-foreground)", fontSize: 11 }}
            tickFormatter={(capturedAt: string) =>
              formatCaptureTick(capturedAt, perCapture, timeZone)
            }
          />

          <YAxis
            reversed
            domain={[1, yMax]}
            ticks={ticks}
            width={34}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
            tick={{ fill: "var(--color-muted-foreground)", fontSize: 11 }}
            tickFormatter={(rank: number) => `#${rank}`}
          />

          <Tooltip
            cursor={{ stroke: "var(--color-border)" }}
            contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
            labelFormatter={(capturedAt) =>
              formatCaptureLabel(String(capturedAt), perCapture, timeZone)
            }
            formatter={(rank, name) => [
              rank === null || rank === undefined ? "Off page 1" : `#${String(rank)}`,
              nameByKey.get(String(name)) ?? "GymBeam product",
            ]}
          />

          {series.map((product) => {
            const color = chartSeriesColorForKey(product.rankProductKey)
            return (
              <Line
                key={product.rankProductKey}
                type="monotone"
                dataKey={product.rankProductKey}
                stroke={color}
                strokeWidth={2}
                connectNulls={false}
                dot={{ r: 3, fill: color, stroke: "var(--color-card)", strokeWidth: 2 }}
                activeDot={{ r: 4.5, fill: color, stroke: "var(--color-card)", strokeWidth: 2 }}
                isAnimationActive={!prefersReducedMotion}
                animationDuration={220}
                animationEasing="ease-out"
              />
            )
          })}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function RankingInsightsRail({
  opportunities,
  isOpportunitiesLoading,
  isOpportunitiesError,
  onOpenOpportunities,
}: {
  opportunities: SearchRankingTitleOpportunity[] | undefined
  isOpportunitiesLoading: boolean
  isOpportunitiesError: boolean
  onOpenOpportunities: (() => void) | undefined
}) {
  return (
    <aside className="min-w-0 border-t border-border/70 pt-4 xl:border-l xl:border-t-0 xl:pl-5 xl:pt-0">
      <TopTitleOpportunities
        opportunities={opportunities}
        isLoading={isOpportunitiesLoading}
        isError={isOpportunitiesError}
        onOpen={onOpenOpportunities}
      />
    </aside>
  )
}

function TopTitleOpportunities({
  opportunities,
  isLoading,
  isError,
  onOpen,
}: {
  opportunities: SearchRankingTitleOpportunity[] | undefined
  isLoading: boolean
  isError: boolean
  onOpen: (() => void) | undefined
}) {
  const topOpportunities = opportunities?.slice(0, 5)
  let emptyMessage = "No supported terms in this period"
  if (isLoading) {
    emptyMessage = "Loading competitor terms…"
  } else if (isError) {
    emptyMessage = "Competitor terms unavailable"
  }

  return (
    <section>
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="text-[13px] font-semibold">Top 5 unused competitor terms</h4>

        {opportunities ? (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {`${opportunities.length} total`}
          </span>
        ) : null}
      </div>

      {topOpportunities && topOpportunities.length > 0 ? (
        <ul className="mt-2 divide-y divide-border/60">
          {topOpportunities.map((opportunity) => (
            <li key={opportunity.phrase}>
              <button
                type="button"
                aria-controls={TITLE_OPPORTUNITIES_SECTION_ID}
                onClick={onOpen}
                disabled={!onOpen}
                className="w-full rounded-lg px-2 py-2 text-left outline-none transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring/40 disabled:cursor-default"
              >
                <span className="block truncate text-xs font-medium text-foreground">
                  {opportunity.phrase}
                </span>

                <span className="mt-0.5 block truncate text-[11px] tabular-nums text-muted-foreground">
                  {`${opportunity.measuredDayCount} ${opportunity.measuredDayCount === 1 ? "day" : "days"} · ${opportunity.competitorProductCount} ${opportunity.competitorProductCount === 1 ? "product" : "products"} · best #${opportunity.bestScreenPosition}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">{emptyMessage}</p>
      )}
    </section>
  )
}

/** Axis tick: clock time when the range is a single day (the date would repeat
 *  on every tick and only the clock carries information), otherwise the date —
 *  a multi-day series holds one point per day, so a clock would be noise. */
function formatCaptureTick(capturedAt: string, perCapture: boolean, timeZone: string): string {
  return perCapture ? formatClock(capturedAt, timeZone) : formatDate(capturedAt.slice(0, 10))
}

/** Tooltip title. On a daily point the clock still matters — it says which
 *  capture of that day the position came from. */
function formatCaptureLabel(capturedAt: string, perCapture: boolean, timeZone: string): string {
  const day = formatLongDate(capturedAt.slice(0, 10))
  const clock = formatClock(capturedAt, timeZone)
  return perCapture ? `${day}, ${clock}` : `${day} · last capture ${clock}`
}

/** Capture clock in the marketplace's reporting zone — the same zone the day
 *  buckets use, so a 00:30 local capture reads as 00:30 on that day rather than
 *  22:30 UTC on the previous one. */
function formatClock(capturedAt: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone,
  }).format(new Date(capturedAt))
}

function formatDate(day: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${day}T00:00:00Z`))
}

function formatLongDate(day: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${day}T00:00:00Z`))
}

export function TitleOpportunitiesPanel({
  opportunities,
  isLoading,
  isError,
  range,
  expanded,
  onExpandedChange,
}: {
  opportunities: SearchRankingTitleOpportunity[] | undefined
  isLoading: boolean
  isError: boolean
  range: SearchRankingDateRange | null
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}) {
  const [isInternallyExpanded, setIsInternallyExpanded] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const isExpanded = expanded ?? isInternallyExpanded
  const visible = showAll ? opportunities : opportunities?.slice(0, 10)
  const setExpanded = (nextExpanded: boolean) => {
    if (expanded === undefined) {
      setIsInternallyExpanded(nextExpanded)
    }
    onExpandedChange?.(nextExpanded)
  }
  const countLabel = titleOpportunityCountLabel(opportunities, isLoading, isError)
  return (
    <section
      id={TITLE_OPPORTUNITIES_SECTION_ID}
      className="scroll-mt-6 rounded-xl border border-border bg-card p-5 shadow-card"
    >
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3>
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-controls={TITLE_OPPORTUNITIES_CONTENT_ID}
              onClick={() => setExpanded(!isExpanded)}
              className="inline-flex items-center gap-2 rounded-lg text-left text-[15px] font-semibold tracking-tight outline-none transition-colors hover:text-info focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              Competitor terms missing from our titles
              <ChevronDown
                className={cn(
                  "size-4 shrink-0 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none",
                  isExpanded && "rotate-180"
                )}
              />
            </button>
          </h3>

          <p className="mt-1 max-w-3xl text-xs text-muted-foreground">
            Title vocabulary found in measured competitor cards but absent from GymBeam cards. These
            are evidence-backed wording opportunities, not search-volume estimates.
          </p>
        </div>

        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
          {`${countLabel}${range ? ` · ${formatDate(range.from)}–${formatDate(range.to)}` : ""}`}
        </span>
      </header>

      <div id={TITLE_OPPORTUNITIES_CONTENT_ID}>
        {isExpanded ? (
          <>
            {titleOpportunityContent({ visible, isLoading, isError })}

            {opportunities && opportunities.length > 10 ? (
              <button
                type="button"
                onClick={() => setShowAll((value) => !value)}
                className="mt-3 rounded-full px-3 py-1.5 text-xs font-medium text-info outline-none transition-colors hover:bg-info/10 focus-visible:ring-2 focus-visible:ring-ring/40"
              >
                {showAll ? "Show top 10" : `Show all ${opportunities.length}`}
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  )
}

function titleOpportunityCountLabel(
  opportunities: SearchRankingTitleOpportunity[] | undefined,
  isLoading: boolean,
  isError: boolean
): string {
  if (isLoading) {
    return "Loading terms"
  }
  if (isError) {
    return "Unavailable"
  }
  const count = opportunities?.length ?? 0
  return `${count} ${count === 1 ? "term" : "terms"}`
}

function titleOpportunityContent({
  visible,
  isLoading,
  isError,
}: {
  visible: SearchRankingTitleOpportunity[] | undefined
  isLoading: boolean
  isError: boolean
}): ReactNode {
  if (isLoading) {
    return (
      <div className="mt-4 space-y-2" aria-label="Loading title opportunities">
        {[0, 1, 2].map((row) => (
          <div key={row} className="h-14 animate-pulse rounded-lg bg-muted/60" />
        ))}
      </div>
    )
  }
  if (isError) {
    return (
      <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
        Title opportunities could not be loaded.
      </p>
    )
  }
  if (visible && visible.length > 0) {
    return (
      <div className="mt-4 divide-y divide-border/70">
        {visible.map((opportunity) => (
          <TitleOpportunityRow key={opportunity.phrase} opportunity={opportunity} />
        ))}
      </div>
    )
  }
  return (
    <p className="mt-4 rounded-lg bg-muted/40 px-3 py-4 text-center text-sm text-muted-foreground">
      No missing competitor terms were supported by this period&apos;s measured evidence.
    </p>
  )
}

function TitleOpportunityRow({ opportunity }: { opportunity: SearchRankingTitleOpportunity }) {
  const measuredDays = `${opportunity.measuredDayCount} measured ${opportunity.measuredDayCount === 1 ? "day" : "days"}`
  const competitorProducts = `${opportunity.competitorProductCount} competitor ${opportunity.competitorProductCount === 1 ? "product" : "products"}`
  return (
    <article className="grid gap-3 py-3 first:pt-0 last:pb-0 lg:grid-cols-[minmax(12rem,0.7fr)_minmax(0,1.3fr)] lg:items-start">
      <div className="min-w-0">
        <h4 className="font-medium text-foreground">{opportunity.phrase}</h4>

        <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
          <EvidencePill>{measuredDays}</EvidencePill>

          <EvidencePill>{competitorProducts}</EvidencePill>

          <EvidencePill>{`best screen #${opportunity.bestScreenPosition}`}</EvidencePill>

          {opportunity.organic ? <EvidencePill>Organic</EvidencePill> : null}

          {opportunity.sponsored ? <EvidencePill>Sponsored</EvidencePill> : null}
        </div>
      </div>

      <ul className="min-w-0 space-y-1.5">
        {opportunity.examples.map((example) => (
          <li key={`${example.url}-${example.name}`} className="min-w-0 text-xs">
            <a
              href={example.url}
              target="_blank"
              rel="noreferrer"
              className="group flex min-w-0 items-center gap-2 text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              <span className="shrink-0 tabular-nums">#{example.bestScreenPosition}</span>

              <span className="truncate group-hover:underline">{example.name}</span>

              <ExternalLink className="size-3 shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </article>
  )
}

function EvidencePill({ children }: { children: ReactNode }) {
  return <span className="rounded-full bg-muted px-2 py-1 tabular-nums">{children}</span>
}

const CHANNEL_FILTER = {
  all: "all",
  organic: "organic",
  sponsored: "sponsored",
  ours: "ours",
} as const

type ChannelFilter = (typeof CHANNEL_FILTER)[keyof typeof CHANNEL_FILTER]

/** The first page as it was rendered: one list in screen order, paid and unpaid
 *  interleaved exactly the way the shopper met them. Two side-by-side lists
 *  ranked per channel could not show that — an ad at screen slot 2 and the
 *  organic result it pushed to slot 5 only read as adjacent here.
 *
 *  The filter narrows the same list; it never re-ranks it. */
export function FirstPagePanel({
  detail,
  organicCount,
  paidCount,
  onSelectRun,
  onSelectProduct,
  isSwitching,
}: {
  detail: SearchRankingKeywordDetail
  organicCount: number
  paidCount: number
  onSelectRun?: (runId: string) => void
  onSelectProduct?: (card: DetailCard) => void
  isSwitching: boolean
}) {
  const [filter, setFilter] = useState<ChannelFilter>(CHANNEL_FILTER.all)
  const inScreenOrder = [...detail.cards].sort((a, b) => a.screenSlot - b.screenSlot)
  const filtered = inScreenOrder.filter((card) => {
    if (filter === CHANNEL_FILTER.all) {
      return true
    }
    if (filter === CHANNEL_FILTER.ours) {
      return card.isOwnBrand
    }
    return filter === CHANNEL_FILTER.sponsored ? card.sponsored : !card.sponsored
  })

  return (
    <section className="min-w-0 overflow-hidden rounded-xl border border-border bg-card px-4 py-3 shadow-card">
      <header className="flex flex-col gap-3 border-b border-border pb-2.5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <h3 className="text-xs font-semibold">First page snapshot</h3>

          <span className="text-[11px] text-muted-foreground">
            {`${detail.cards.length} cards in screen order · ${organicCount} organic · ${paidCount} ${detail.marketplace.sponsoredLabel.toLowerCase()}`}
          </span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <SegmentedControl
            options={[
              { value: CHANNEL_FILTER.all, label: "All" },
              { value: CHANNEL_FILTER.organic, label: "Organic" },
              { value: CHANNEL_FILTER.sponsored, label: detail.marketplace.sponsoredLabel },
              { value: CHANNEL_FILTER.ours, label: "Our products" },
            ]}
            value={filter}
            onChange={setFilter}
            label="Card channel"
          />

          {onSelectRun ? (
            <SnapshotStepper
              history={detail.history}
              runId={detail.runId}
              timeZone={detail.marketplace.reportingTimeZone}
              isSwitching={isSwitching}
              onSelectRun={onSelectRun}
            />
          ) : null}
        </div>
      </header>

      {filtered.length === 0 ? (
        <p className="py-6 text-center text-xs text-muted-foreground">
          {snapshotEmptyMessage(filter, detail.marketplace.sponsoredLabel)}
        </p>
      ) : (
        <ul className={cn("flex flex-col", isSwitching && "opacity-60")}>
          {snapshotRows(filtered, onSelectProduct)}
        </ul>
      )}
    </section>
  )
}

function snapshotEmptyMessage(filter: ChannelFilter, sponsoredLabel: string): string {
  if (filter === CHANNEL_FILTER.ours) {
    return "No GymBeam products on this capture."
  }
  if (filter === CHANNEL_FILTER.sponsored) {
    return `No ${sponsoredLabel.toLowerCase()} cards on this capture.`
  }
  if (filter === CHANNEL_FILTER.organic) {
    return "No organic cards on this capture."
  }
  return "No cards on this capture."
}

/** Steps between days, landing on each day's last measured capture.
 *
 *  Deliberately per day and not per capture: with three runs a day a per-capture
 *  stepper is two dozen clicks of mostly identical pages, and the day's last
 *  capture is already the one the trend line and the Change column read. Any
 *  capture can still be reached by URL — this is the browsing affordance, not the
 *  only way in.
 *
 *  Hidden below two days, because there is nothing to step to. */
function SnapshotStepper({
  history,
  runId,
  timeZone,
  isSwitching,
  onSelectRun,
}: {
  history: SearchRankingCapturePoint[]
  runId: string
  timeZone: string
  isSwitching: boolean
  onSelectRun: (runId: string) => void
}) {
  const days = measuredDays(history)
  if (days.length < 2) {
    return null
  }
  // A capture reached by URL need not be its day's last one; fall back to the day
  // it belongs to so the stepper still knows where it is.
  const current = days.find((point) => point.runId === runId)
  const index = current
    ? days.indexOf(current)
    : days.findIndex((point) => point.day === history.find((p) => p.runId === runId)?.day)
  if (index === -1) {
    return null
  }
  const older = days[index - 1]
  const newer = days[index + 1]
  const shown = days[index]
  return (
    <div className="flex items-center gap-1.5" role="group" aria-label="Capture day">
      <StepperButton
        label="Previous day"
        disabled={older === undefined || isSwitching}
        onClick={() => older && onSelectRun(older.runId)}
      >
        <ChevronLeft className="size-3.5" />
      </StepperButton>

      <span
        className="min-w-0 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground"
        title={
          shown
            ? `Last capture of ${formatLongDate(shown.day)}, ${formatClock(shown.capturedAt, timeZone)}`
            : undefined
        }
      >
        {shown ? `${formatDate(shown.day)} · ${index + 1}/${days.length}` : ""}
      </span>

      <StepperButton
        label="Next day"
        disabled={newer === undefined || isSwitching}
        onClick={() => newer && onSelectRun(newer.runId)}
      >
        <ChevronRight className="size-3.5" />
      </StepperButton>
    </div>
  )
}

function StepperButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex size-6 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-35"
    >
      {children}
    </button>
  )
}

/** Rows in screen order. The leading number is the slot on the page; the channel
 *  rank sits next to it, because "#2 of the ads" and "2nd thing you see" are
 *  different facts and the table has to carry both. */
function snapshotRows(
  cards: DetailCard[],
  onSelectProduct: ((card: DetailCard) => void) | undefined
) {
  return cards.map((card) => {
    const channelRank = card.sponsored ? card.sponsoredRank : card.organicRank
    return (
      <li key={card.screenSlot}>
        <div
          className={cn(
            "flex items-baseline gap-2.5 border-b border-border/60 py-1.5 text-[13px] last:border-b-0",
            card.isOwnBrand && "-mx-2 rounded-md bg-brand/10 px-2 ring-1 ring-inset ring-brand/20"
          )}
        >
          <span className="w-7 shrink-0 text-right font-semibold tabular-nums">
            {card.screenSlot}
          </span>

          <span
            className={cn(
              // Fixed width so the arrow column behind it stays on one axis down
              // the list, and wide enough for the longest label this can hold:
              // "org 60" measures 48.4px, and a two-digit rank wrapped at w-11.
              // `whitespace-nowrap` keeps a wider font from folding it again.
              "w-13 shrink-0 whitespace-nowrap rounded-sm px-1 py-px text-center text-[10px] font-semibold uppercase leading-normal tracking-wide",
              card.sponsored ? "bg-warning/15 text-warning" : "bg-muted/60 text-muted-foreground"
            )}
            title={card.sponsored ? "Paid placement" : "Unpaid result"}
          >
            {`${card.sponsored ? "ad" : "org"} ${channelRank ?? "—"}`}
          </span>

          <span className="w-8 shrink-0 text-right">
            <CardMove card={card} />
          </span>

          {card.isOwnBrand ? <GymBeamMark className="h-3 w-auto" title="Our product" /> : null}

          <CardName card={card} onSelectProduct={onSelectProduct} />

          <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
            {card.priceCurrent === null
              ? "—"
              : `${card.priceCurrent.toFixed(2)} ${card.currency ?? ""}`}
          </span>
        </div>
      </li>
    )
  })
}

function CardName({
  card,
  onSelectProduct,
}: {
  card: DetailCard
  onSelectProduct: ((card: DetailCard) => void) | undefined
}) {
  const canSelect = card.isOwnBrand && card.rankProductKey !== null && onSelectProduct !== undefined
  return (
    <span className="inline-flex min-w-0 flex-1 items-center gap-1">
      {canSelect ? (
        <button
          type="button"
          title={card.name}
          aria-label={`Show ranking for ${card.name}`}
          onClick={() => onSelectProduct?.(card)}
          className="min-w-0 truncate rounded-sm text-left font-semibold text-brand outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          {card.name}
        </button>
      ) : (
        <span
          title={card.name}
          className={cn(
            "min-w-0 truncate",
            card.isOwnBrand ? "font-semibold text-brand" : "text-foreground"
          )}
        >
          {card.name}
        </span>
      )}

      {card.isOwnBrand && card.matchedSku !== null ? (
        <a
          href={`/products/${encodeURIComponent(card.matchedSku)}`}
          aria-label={`Open ${card.name} in Products`}
          title={`Open ${card.name} in Products`}
          onClick={(event) => event.stopPropagation()}
          className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <ExternalLink className="size-3" aria-hidden="true" />
        </a>
      ) : null}
    </span>
  )
}

// Day-over-day movement within the card's own channel ranking.
function CardMove({ card }: { card: DetailCard }) {
  if (card.isNew) {
    return (
      <span
        className="text-[11px] font-semibold text-info"
        title="Was not on the previous measured first page"
      >
        new
      </span>
    )
  }
  if (card.rankChange === null) {
    return <span className="text-muted-foreground/40">—</span>
  }
  if (card.rankChange === 0) {
    return <span className="text-muted-foreground/60">·</span>
  }
  return (
    <span
      className={cn(
        "text-xs font-semibold tabular-nums",
        card.rankChange > 0 ? "text-success" : "text-destructive"
      )}
      title={CARD_MOVE_HINT}
    >
      {card.rankChange > 0 ? `▲ ${card.rankChange}` : `▼ ${-card.rankChange}`}
    </span>
  )
}
